import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  ILeague,
  ILeagueMatch,
  ILeagueMatchList,
  ITeam,
} from '../../types/types';
import { API_ORIGIN } from 'app/consts';

export const api = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({ baseUrl: API_ORIGIN }),
  endpoints: (builder) => ({
    getLeagues: builder.query<ILeague[], void>({
      query: () => '/leagues/',
      transformResponse: (response: ILeague[]) =>
        [...response].sort((a, b) => {
          const aRaw = a.attrs.ageCategory;
          const bRaw = b.attrs.ageCategory;
          if (!aRaw && !bRaw) return 0;
          if (!aRaw) return 1;
          if (!bRaw) return -1;
          const aNum = Number(aRaw.replace(/\D/g, ''));
          const bNum = Number(bRaw.replace(/\D/g, ''));
          return aNum - bNum;
        }),
    }),
    getLeague: builder.query({
      query: ({ league_id }: { league_id: string }) => `/leagues/${league_id}/`,
    }),
    getLeagueMatches: builder.query<ILeagueMatchList[], { league_id: string }>({
      query: ({ league_id }) => `/leagues/${league_id}/matches/`,
    }),
    getMatch: builder.query<
      ILeagueMatch,
      { league_id: string; match_id: string }
    >({
      query: ({ league_id, match_id }) =>
        `/leagues/${league_id}/matches/${match_id}/`,
    }),
    getMatchRuns: builder.query({
      query: ({
        league_id,
        match_id,
      }: {
        league_id: string;
        match_id: string;
      }) => `/leagues/${league_id}/matches/${match_id}/runs/`,
    }),
    getLeagueFullMatches: builder.query({
      async queryFn({ league_id }, _queryApi, _extraOptions, fetchWithBQ) {
        const matchesRes = await fetchWithBQ(`/leagues/${league_id}/matches/`);
        if (matchesRes.error) return { error: matchesRes.error };
        const matches = (matchesRes.data as any[]) ?? [];
        const detailResponses = await Promise.all(
          matches.map((m) =>
            fetchWithBQ(`/leagues/${league_id}/matches/${m.id}/`),
          ),
        );
        const fullMatches = detailResponses.map((res, index) => {
          if (res.error) {
            return { ...matches[index], error: res.error };
          }
          return res.data;
        });
        return { data: fullMatches };
      },
    }),
    getLeagueTeams: builder.query<ITeam[], { league_id: string }>({
      query: ({ league_id }) => `/leagues/${league_id}/teams/`,
    }),
  }),
});

export const {
  useGetLeaguesQuery,
  useGetLeagueQuery,
  useGetLeagueMatchesQuery,
  useLazyGetLeagueMatchesQuery,
  useGetMatchQuery,
  useGetMatchRunsQuery,
  useGetLeagueFullMatchesQuery,
  useGetLeagueTeamsQuery,
  useLazyGetLeagueTeamsQuery,
} = api;
